import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "reducers";

//Component
import {
  DataGrid,
  GridColDef,
  GridColumns,
  GridToolbarContainer,
} from "@material-ui/data-grid";
import CustomLoadingOverlay from "components/DataGridLoadingOverlay";
import CustomNoRowsOverlay from "components/DataGridNoRowsOverlay";
import { ARIA_SuccessCode, Hotel } from "models";
import { DataGridFilterToolbar } from "components/DataGridFilterToolbar";
import { GridColFilterDef } from "components/DataGridFilterSelection";
//Icon

//Helper
import { convertDateTimeToString } from "utils/dateHelper";
import { Data } from "react-csv/components/CommonPropTypes";
import { ExportButton } from "components/ExportButton";
import { MainDrawer } from "components/MainDrawer";
import { AccessControlButton } from "components";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from "@material-ui/core";
import {
  PM_ADD_HOTEL_CODE,
  PM_DELETE_HOTEL_CODE,
  PM_EDIT_HOTEL_CODE,
} from "models/backend/constant/PermissionCode";
import {
  createHotelAPI,
  deleteHotelAPI,
  getHotelListAPI,
  updateHotelAPI,
} from "services/hotelService";
import { fetchConfigurationGroupList } from "reducers/configurationGroup";
import { fetchLanguageSettingList } from "reducers/languageSetting";
import {
  GRID_FILTER_TYPE_COUNTRY,
  GRID_FILTER_TYPE_HOTELGROUP,
} from "components/DataGridFilterSelection/type";

export const HotelManagementScreen = () => {
  const dispatch = useDispatch();

  const { configurationGroupList } = useSelector(
    (state: RootState) => state.configurationGroup
  );
  const { languageSettingList } = useSelector(
    (state: RootState) => state.languageSettingState
  );
  const { countryList } = useSelector((state: RootState) => state.country);
  const { hotelGroupList } = useSelector(
    (state: RootState) => state.hotelGroupState
  );
  const { selectedHotel } = useSelector((state: RootState) => state.hotelState);

  const [index, setIndex] = useState<number>(1);
  const [size, setSize] = useState<number>(10);
  const [row, setRow] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [requestDateTime, setRequestDateTime] = useState<Date>(new Date());

  const [hotelManagementList, setHotelManagementList] = useState<Hotel[]>([]);
  const [exportList, setExportList] = useState<Data>([]);
  const [filterList, setFilterList] = useState<GridColFilterDef[]>([]);

  const [loadList, setloadList] = useState(true);

  const [addHotel, setAddHotel] = useState<any | undefined>();
  const [editHotel, setEditHotel] = useState<Hotel | undefined>();
  const [removeHotel, setRemoveHotel] = useState<Hotel | undefined>();
  const [deleteConfirmation, setDeleteConfirmation] = useState("");

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  //Setting up the each column header item
  const hotelCodeHeader: GridColDef = {
    field: "code",
    headerName: "Code",
    sortable: false,
    renderHeader: (params: any) => <strong>{"Code"}</strong>,
    minWidth: 120,
  };

  const hotelNameHeader: GridColDef = {
    field: "name",
    headerName: "Name",
    sortable: false,
    renderHeader: () => <strong>{"Name"}</strong>,
    minWidth: 200,
    flex: 1,
  };

  const hotelDomainUrlHeader: GridColDef = {
    field: "domainUrl",
    headerName: "Domain Url",
    sortable: false,
    renderHeader: () => <strong>{"Domain Url"}</strong>,
    minWidth: 180,
    flex: 1,
  };

  const hotelGroupHeader: GridColDef = {
    field: "hotelGroupId",
    headerName: "Hotel Group",
    sortable: false,
    type: GRID_FILTER_TYPE_HOTELGROUP,
    renderHeader: () => <strong>{"Hotel Group"}</strong>,
    valueGetter: (params) => {
      if (params.value === 0) {
        return "-";
      } else {
        const hotelGroup = hotelGroupList.find((hg) => hg.id == params.value);
        return hotelGroup?.name;
      }
    },
    minWidth: 200,
    flex: 1,
  };

  const hotelCountryHeader: GridColDef = {
    field: "countryId",
    headerName: "Country",
    sortable: false,
    type: GRID_FILTER_TYPE_COUNTRY,
    renderHeader: () => <strong>{"Country"}</strong>,
    valueGetter: (params) => {
      if (params.value === 0) {
        return "-";
      } else {
        const _country = countryList.find((c) => c.id == params.value);
        if (_country != undefined) {
          return _country.name;
        }

        return "-";
      }
    },
    width: 160,
    // flex: 1,
  };

  const actionsHeader: GridColDef = {
    field: "id",
    headerName: "Actions",
    minWidth: 240,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    renderHeader: () => <strong>{"Actions"}</strong>,
    renderCell: (params: any) => (
      <strong>
        <AccessControlButton
          code={PM_EDIT_HOTEL_CODE}
          variant="contained"
          color="primary"
          size="small"
          value={params.value}
          onClick={handleOpenEditDialog}
        >
          Edit
        </AccessControlButton>
        <AccessControlButton
          code={PM_ADD_HOTEL_CODE}
          variant="contained"
          color="primary"
          size="small"
          value={params.value}
          style={{ marginLeft: 12 }}
          onClick={handleDuplicateFunctionClick}
        >
          Clone
        </AccessControlButton>
        <AccessControlButton
          code={PM_DELETE_HOTEL_CODE}
          variant="contained"
          color="primary"
          size="small"
          value={params.value}
          style={{ marginLeft: 12 }}
          onClick={handleOpenDeleteDialog}
        >
          Delete
        </AccessControlButton>
      </strong>
    ),
  };

  const headerColumn: GridColumns = [
    hotelNameHeader,
    hotelCodeHeader,
    hotelGroupHeader,
    hotelCountryHeader,
    hotelDomainUrlHeader,
    actionsHeader,
  ];

  React.useEffect(() => {
    dispatch(fetchConfigurationGroupList());
    dispatch(fetchLanguageSettingList());
  }, []);

  React.useEffect(() => {
    if (selectedHotel != undefined && loadList) {
      setIsLoading(true);
      setHotelManagementList([]);
      setRequestDateTime(new Date());

      getHotelListAPI({
        filter: filterList,
        pageIndex: index,
        pageSize: size,
        // orderBy: "module",
      })
        .then((response) => {
          if (response.resultCode === ARIA_SuccessCode) {
            setHotelManagementList(response.result.items);
            setRow(response.result.rowCount);
          }
        })
        .finally(() => {
          setloadList(false);
          setIsLoading(false);
        });
    }
  }, [index, size, filterList, selectedHotel, loadList]);

  const getExportTransaction = async () => {
    setIsLoading(true);
    var _exportIndex = 1;
    var exportList: Hotel[] = [];

    try {
      do {
        var response = await getHotelListAPI({
          filter: filterList,
          pageIndex: _exportIndex,
          pageSize: size,
          //   orderBy: "module",
        });

        exportList.push(...response.result.items);

        _exportIndex++;
      } while (response.result.pageCount >= _exportIndex);

      setExportList(exportList);
      setIsExporting(true);
      setIsLoading(false);
    } catch (err) {
      console.log("Oposs.. Something is wrong");
    }
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <DataGridFilterToolbar
          headerColumns={headerColumn}
          filterList={filterList}
          onFilterApply={(items) => {
            setFilterList(items.slice());
            setloadList(true);
          }}
        />
        <ExportButton
          filename="Hotel"
          data={exportList}
          headers={[
            { label: "Name", key: "name" },
            { label: "Code", key: "code" },
            { label: "Module", key: "module" },
            { label: "Method", key: "httpMethod" },
            { label: "Path", key: "path" },
            { label: "Authorized", key: "identityRoles" },
          ]}
          readyToExport={isExporting}
          onClick={getExportTransaction}
          onFinishDownload={() => {
            setExportList([]);
            setIsExporting(false);
          }}
        >
          Export
        </ExportButton>
        <div style={{ flexGrow: 1 }}></div>
        <AccessControlButton
          code={PM_ADD_HOTEL_CODE}
          variant="contained"
          color="primary"
          size="small"
          onClick={handleOpenCreateDialog}
        >
          New Hotel
        </AccessControlButton>
        <div style={{ paddingLeft: "8px" }}>
          <b>Data as at </b>: {convertDateTimeToString(requestDateTime)}
        </div>
      </GridToolbarContainer>
    );
  }

  const handleOpenCreateDialog = () => {
    setOpenEditDialog(true);
  };

  const handleDuplicateFunctionClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    var _updateFunction = hotelManagementList.find(
      (f) => f.id === parseInt(event.currentTarget.value)
    );

    setAddHotel(_updateFunction);
    setOpenEditDialog(true);
  };

  const handleOpenEditDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    var updateFunction = hotelManagementList.find(
      (f) => f.id === parseInt(event.currentTarget.value)
    );
    setEditHotel(updateFunction);
    // setSelectedIsAudited(updateFunction.IsAudited)
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    var _removeFunction = hotelManagementList.find(
      (f) => f.id === parseInt(event.currentTarget.value)
    );
    setRemoveHotel(_removeFunction);
    setOpenDeleteDialog(true);
  };

  const handleEditOrAdd = () => {
    if (editHotel == null) {
      if (
        addHotel === null ||
        addHotel.name === null ||
        addHotel.name === "" ||
        addHotel.code === null ||
        addHotel.code === "" ||
        addHotel.configurationGroupId === 0 ||
        addHotel.countryId === 0 ||
        addHotel.hotelGroupId === 0 ||
        addHotel.languageSettingId === 0
      ) {
        alert("Please fill up the details below to add a new hotel.");
      } else {
        createHotelAPI({ ...addHotel })
          .catch((err) => {
            alert(err);
          })
          .finally(() => {
            setloadList(true);
            handleEditClose();
          });
      }
    } else {
      if (
        editHotel.name === null ||
        editHotel.name === "" ||
        editHotel.code === null ||
        editHotel.code === "" ||
        editHotel.configurationGroupId === 0 ||
        editHotel.countryId === 0 ||
        editHotel.hotelGroupId === 0 ||
        editHotel.languageSettingId === 0
      ) {
        alert("Please fill up the details below to edit hotel.");
      } else {
        updateHotelAPI({ ...editHotel })
          .catch((err) => {
            alert(err);
          })
          .finally(() => {
            setloadList(true);
            handleEditClose();
          });
      }
    }
  };

  const handleDelete = () => {
    if (deleteConfirmation !== removeHotel?.name) {
      alert("Please key in the hotel name to confirm deletion.");
    } else {
      deleteHotelAPI({ ...removeHotel })
        .catch((err) => {
          alert(err);
        })
        .finally(() => {
          setDeleteConfirmation("");
          setloadList(true);
          handleEditClose();
        });
    }
  };

  const handleEditClose = () => {
    setAddHotel(undefined);
    setEditHotel(undefined);
    setRemoveHotel(undefined);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  return (
    <>
      <MainDrawer title="Hotel Management">
        {/* Add and Edit Dialog */}
        <Dialog
          open={openEditDialog}
          onClose={handleEditClose}
          aria-labelledby="form-dialog-title"
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle id="form-dialog-title">
            {editHotel != undefined ? "Edit Hotel" : "Add Hotel"}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {editHotel != undefined
                ? "Change the detail below and save it to update hotel details, ."
                : "Fill up the details below to add a new hotel."}
            </DialogContentText>
            <form noValidate autoComplete="off">
              <div>
                <TextField
                  margin="dense"
                  id="name"
                  label="Hotel Name"
                  type="text"
                  value={
                    editHotel != undefined
                      ? editHotel.name
                      : addHotel?.name != undefined
                      ? addHotel?.name
                      : ""
                  }
                  onChange={(e) => {
                    editHotel != undefined
                      ? setEditHotel({
                          ...editHotel,
                          name: e.currentTarget.value,
                        })
                      : setAddHotel({
                          ...addHotel,
                          name: e.currentTarget.value,
                        });
                  }}
                  fullWidth
                />
                <TextField
                  margin="dense"
                  id="code"
                  label="Code"
                  type="text"
                  value={
                    editHotel != undefined
                      ? editHotel.code
                      : addHotel?.code != undefined
                      ? addHotel?.code
                      : ""
                  }
                  onChange={(e) => {
                    editHotel != undefined
                      ? setEditHotel({
                          ...editHotel,
                          code: e.currentTarget.value,
                        })
                      : setAddHotel({
                          ...addHotel,
                          code: e.currentTarget.value,
                        });
                  }}
                  fullWidth
                />
                {hotelGroupList.length > 0 ? (
                  <TextField
                    margin="dense"
                    id="hotelGroup"
                    label="Hotel Group"
                    select
                    value={
                      editHotel != undefined
                        ? editHotel.hotelGroupId
                        : addHotel?.hotelGroupId != undefined
                        ? addHotel?.hotelGroupId
                        : ""
                    }
                    onChange={(e) => {
                      editHotel != undefined
                        ? setEditHotel({
                            ...editHotel,
                            hotelGroupId: parseInt(e.target.value),
                          })
                        : setAddHotel({
                            ...addHotel,
                            hotelGroupId: parseInt(e.target.value),
                          });
                    }}
                    fullWidth
                  >
                    {hotelGroupList.map((hotelGroup, index) => (
                      <MenuItem key={index} value={hotelGroup.id}>
                        {hotelGroup.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}

                {countryList.length > 0 ? (
                  <TextField
                    margin="dense"
                    id="country"
                    label="Country"
                    select
                    value={
                      editHotel != undefined
                        ? editHotel.countryId
                        : addHotel?.countryId != undefined
                        ? addHotel?.countryId
                        : ""
                    }
                    onChange={(e) => {
                      editHotel != undefined
                        ? setEditHotel({
                            ...editHotel,
                            countryId: parseInt(e.target.value),
                          })
                        : setAddHotel({
                            ...addHotel,
                            countryId: parseInt(e.target.value),
                          });
                    }}
                    fullWidth
                  >
                    {countryList.map((country, index) => (
                      <MenuItem key={index} value={country.id}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}

                {configurationGroupList.length > 0 ? (
                  <TextField
                    margin="dense"
                    id="configGroup"
                    label="Configuration Group"
                    select
                    value={
                      editHotel != undefined
                        ? editHotel.configurationGroupId
                        : addHotel?.configurationGroupId != undefined
                        ? addHotel?.configurationGroupId
                        : ""
                    }
                    onChange={(e) => {
                      editHotel != undefined
                        ? setEditHotel({
                            ...editHotel,
                            configurationGroupId: parseInt(e.target.value),
                          })
                        : setAddHotel({
                            ...addHotel,
                            configurationGroupId: parseInt(e.target.value),
                          });
                    }}
                    fullWidth
                  >
                    {configurationGroupList.map((configGroup, index) => (
                      <MenuItem key={index} value={configGroup.id}>
                        {configGroup.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}

                {languageSettingList.length > 0 ? (
                  <TextField
                    margin="dense"
                    id="languageSetting"
                    label="Language Setting"
                    select
                    value={
                      editHotel != undefined
                        ? editHotel.languageSettingId
                        : addHotel?.languageSettingId != undefined
                        ? addHotel?.languageSettingId
                        : ""
                    }
                    onChange={(e) => {
                      editHotel != undefined
                        ? setEditHotel({
                            ...editHotel,
                            languageSettingId: parseInt(e.target.value),
                          })
                        : setAddHotel({
                            ...addHotel,
                            languageSettingId: parseInt(e.target.value),
                          });
                    }}
                    fullWidth
                  >
                    {languageSettingList.map((languageSetting, index) => (
                      <MenuItem key={index} value={languageSetting.id}>
                        {languageSetting.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}
                <TextField
                  margin="dense"
                  id="domainUrl"
                  label="Domain Url"
                  type="text"
                  value={
                    editHotel != undefined
                      ? editHotel.domainUrl
                      : addHotel?.domainUrl != undefined
                      ? addHotel?.domainUrl
                      : ""
                  }
                  onChange={(e) => {
                    editHotel != undefined
                      ? setEditHotel({
                          ...editHotel,
                          domainUrl: e.currentTarget.value,
                        })
                      : setAddHotel({
                          ...addHotel,
                          domainUrl: e.currentTarget.value,
                        });
                  }}
                  fullWidth
                />

                {editHotel != undefined ? (
                  <TextField
                    margin="dense"
                    id="apiKey"
                    label="Api Key"
                    type="text"
                    disabled
                    value={editHotel.apiKey}
                    fullWidth
                  />
                ) : null}
              </div>
            </form>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditClose} color="primary">
              Cancel
            </Button>
            <Button onClick={handleEditOrAdd} color="primary">
              {editHotel != null ? `Save & Update` : `Add Hotel`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* delete Dialog */}
        {removeHotel != undefined ? (
          <Dialog
            open={openDeleteDialog}
            onClose={handleEditClose}
            aria-labelledby="form-dialog-title"
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle id="form-dialog-title">Delete Hotel</DialogTitle>
            <DialogContent>
              <form noValidate autoComplete="off">
                <div>
                  <TextField
                    margin="dense"
                    id="name"
                    label="Hotel Name"
                    type="text"
                    disabled
                    value={removeHotel.name}
                    fullWidth
                  />
                  <TextField
                    margin="dense"
                    id="code"
                    label="Code"
                    type="text"
                    disabled
                    value={removeHotel.code}
                    fullWidth
                  />

                  <DialogContentText
                    color="textPrimary"
                    style={{ marginTop: "18px" }}
                  >
                    {`Are you sure you want to delete ${removeHotel.name}?`}
                  </DialogContentText>
                  <DialogContentText>
                    Please type the following to confirm:
                  </DialogContentText>
                  <DialogContentText>
                    <i>
                      <b>{removeHotel.name}</b>
                    </i>
                  </DialogContentText>
                  <TextField
                    margin="dense"
                    variant="outlined"
                    id="name"
                    type="text"
                    value={deleteConfirmation}
                    onChange={(e) =>
                      setDeleteConfirmation(e.currentTarget.value)
                    }
                    fullWidth
                  />
                </div>
              </form>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleEditClose} color="primary">
                Cancel
              </Button>
              <Button onClick={handleDelete} color="primary">
                {`Delete`}
              </Button>
            </DialogActions>
          </Dialog>
        ) : null}

        {/* Data Grid */}
        <div style={{ height: "80vh" }}>
          <div style={{ display: "flex", height: "100%" }}>
            <div style={{ flexGrow: 1 }}>
              <DataGrid
                pageSize={size}
                rowsPerPageOptions={[10, 20, 30]}
                onPageSizeChange={(size) => {
                  setSize(size);
                  setloadList(true);
                }}
                disableColumnMenu
                density="compact"
                components={{
                  LoadingOverlay: CustomLoadingOverlay,
                  NoRowsOverlay: CustomNoRowsOverlay,
                  Toolbar: CustomToolbar,
                }}
                pagination
                loading={isLoading}
                paginationMode="server"
                rowCount={row}
                filterMode="server"
                onPageChange={(newPage) => {
                  setIndex(++newPage);
                  setloadList(true);
                }}
                rows={hotelManagementList}
                columns={headerColumn}
                getRowId={(row) => row.id}
              />
            </div>
          </div>
        </div>
      </MainDrawer>
    </>
  );
};
